<script setup>
/* eslint-disable vue/no-bare-strings-in-template, prettier/prettier, @intlify/vue-i18n/no-raw-text, vue/attributes-order */
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import Button from 'dashboard/components-next/button/Button.vue';
import WhatsAppSipDialerPanel from './WhatsAppSipDialerPanel.vue';
import { useWhatsAppSipConnection } from 'dashboard/api/telephony_whatsapp_sip/useWhatsAppSipConnection';
import WhatsAppSipMyExtensionAPI from 'dashboard/api/whatsappSip/myExtension';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const { t } = useI18n();
const isDialerOpen = ref(false);

const currentUser = useMapGetter('getCurrentUser');
// Reativo: o backend broadcasta `agent.updated` quando o ramal e
// criado/excluido/deslinkado, atualizando currentUser.whatsapp_sip_extension.
const hasExtension = computed(() =>
  Boolean(currentUser.value?.whatsapp_sip_extension)
);

// Get SIP connection state
const {
  isConnected,
  isConnecting,
  connectionError,
  callState,
  callDirection,
  callNumber,
  remoteDisplayName,
  connect,
} = useWhatsAppSipConnection();

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

// Check if WhatsApp SIP telephony feature is enabled for this account
const isWhatsAppSipEnabled = computed(() => {
  return isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.WHATSAPP_SIP_TELEPHONY
  );
});

// Fetch credentials from Rails and connect (idempotente via guards)
const ensureSipConnection = async () => {
  if (!isWhatsAppSipEnabled.value) return;
  if (!hasExtension.value) return;
  if (isConnected.value) return;
  if (isConnecting.value) return;

  try {
    const { data } = await WhatsAppSipMyExtensionAPI.get();

    if (data.success && data.credentials) {
      await connect({
        extension: data.credentials.extension,
        password: data.credentials.password,
        displayName: data.credentials.display_name,
        sipServer: data.credentials.sip_server,
        wsServer: data.credentials.ws_server,
      });
    }
  } catch (err) {
    // Sem ramal/credentials: nao conecta. hasExtension (computed) reflete o estado.
  }
};

// Conecta no mount (carga inicial)
onMounted(() => {
  if (!isWhatsAppSipEnabled.value) return;

  if (callState.value === 'ringing' && callDirection.value === 'incoming') {
    isDialerOpen.value = true;
  }

  ensureSipConnection();
});

// Conecta quando o ramal e cadastrado/linkado depois do mount (sem F5).
// Sem `immediate`: o onMounted ja cobre a carga inicial. O guard
// isConnected/isConnecting dentro de ensureSipConnection evita dupla conexao.
watch(hasExtension, has => {
  if (has) ensureSipConnection();
});

// Auto-open dialer when incoming call is detected
watch(
  [callState, callDirection],
  ([newCallState, newCallDirection]) => {
    if (newCallState === 'ringing' && newCallDirection === 'incoming') {
      isDialerOpen.value = true;
    }
  },
  { immediate: true }
);

const showDialerLauncher = computed(
  () => isWhatsAppSipEnabled.value && hasExtension.value
);

const prefillNumber = ref('');
const prefillInboxId = ref(null);
const autoCall = ref(false);

const toggleDialer = () => {
  isDialerOpen.value = !isDialerOpen.value;
};

const closeDialer = () => {
  isDialerOpen.value = false;
  prefillNumber.value = '';
  prefillInboxId.value = null;
  autoCall.value = false;
};

const openDialer = payload => {
  if (payload?.number) {
    prefillNumber.value = payload.number;
    prefillInboxId.value = payload.inboxId ?? null;
    autoCall.value = !!payload.autoCall;
  }
  isDialerOpen.value = true;
};

// Handler for incoming call bus event
// Also handles HMR edge cases where composable refs may be stale
const handleIncomingCallEvent = payload => {
  if (payload?.ended) return;

  if (callState.value === 'idle' && payload?.number) {
    callState.value = 'ringing';
    callDirection.value = 'incoming';
    callNumber.value = payload.number;
    remoteDisplayName.value = payload.displayName || null;
  }
  if (!isDialerOpen.value) {
    isDialerOpen.value = true;
  }
};

onMounted(() => {
  emitter.on(BUS_EVENTS.WHATSAPP_SIP_SHOW_DIALER, openDialer);
  emitter.on(BUS_EVENTS.WHATSAPP_SIP_INCOMING_CALL, handleIncomingCallEvent);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.WHATSAPP_SIP_SHOW_DIALER, openDialer);
  emitter.off(BUS_EVENTS.WHATSAPP_SIP_INCOMING_CALL, handleIncomingCallEvent);
});

// Computed for incoming call state (visual feedback on button)
const isIncomingCall = computed(() => {
  return callState.value === 'ringing' && callDirection.value === 'incoming';
});

// Computed for active call state
const hasActiveCall = computed(() => {
  return ['calling', 'ringing', 'connecting', 'connected'].includes(callState.value);
});

// Status tooltip text (i18n, nomenclatura comercial — sem "SIP")
const statusTooltip = computed(() => {
  if (isIncomingCall.value) return t('WHATSAPP_SIP_DIALER.TOOLTIP.INCOMING');
  if (connectionError.value) return t('WHATSAPP_SIP_DIALER.TOOLTIP.ERROR');
  if (isConnecting.value) return t('WHATSAPP_SIP_DIALER.TOOLTIP.CONNECTING');
  if (!isConnected.value) return t('WHATSAPP_SIP_DIALER.TOOLTIP.DISCONNECTED');
  if (hasActiveCall.value) return t('WHATSAPP_SIP_DIALER.TOOLTIP.IN_CALL');
  return t('WHATSAPP_SIP_DIALER.TOOLTIP.CONNECTED');
});

// Icon color based on SIP status
const iconColorClass = computed(() => {
  if (isIncomingCall.value) {
    return '!text-n-ruby-11 animate-bounce';
  }
  if (connectionError.value || (!isConnected.value && !isConnecting.value)) {
    return '!text-n-ruby-11';
  }
  if (isConnecting.value) {
    return '!text-n-amber-11 animate-pulse';
  }
  if (hasActiveCall.value) {
    return '!text-green-600 dark:!text-green-400 animate-pulse';
  }
  return '!text-green-600 dark:!text-green-400';
});
</script>

<template>
  <!-- eslint-disable-next-line vue/no-root-v-if -->
  <div v-if="showDialerLauncher">
    <!-- Inline navbar button -->
    <Button
      v-tooltip="statusTooltip"
      :icon="isIncomingCall ? 'i-lucide-phone-incoming' : 'i-lucide-message-circle'"
      variant="ghost"
      color="slate"
      size="sm"
      class="!size-8 !rounded-lg !bg-transparent hover:!bg-n-alpha-1"
      :class="iconColorClass"
      @click="toggleDialer"
    />

    <!-- Dialer Panel (teleported to body so it stays visible on all routes) -->
    <Teleport to="body">
      <WhatsAppSipDialerPanel
        v-if="isDialerOpen"
        :initial-number="prefillNumber"
        :initial-inbox-id="prefillInboxId"
        :auto-call="autoCall"
        @close="closeDialer"
      />
    </Teleport>
  </div>
</template>
